import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { IoSend, IoBug, IoCheckmarkCircle, IoTrash, IoTime, IoAlert } from 'react-icons/io5';
import { collection, addDoc, query, where, orderBy, onSnapshot, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useUser } from '../context/UserContext';
import NotFound from './NotFound';
import './BetaReport.css';

const SEVERITY = [
  { id: 'low', label: 'Mineur', color: '#22c55e', emoji: '🟢' },
  { id: 'medium', label: 'Moyen', color: '#f59e0b', emoji: '🟡' },
  { id: 'high', label: 'Important', color: '#ef4444', emoji: '🔴' },
  { id: 'critical', label: 'Critique', color: '#991b1b', emoji: '⛔' },
];

const CATEGORIES = [
  'Affichage / UI',
  'Navigation',
  'Lecture',
  'Écriture',
  'Profil / Compte',
  'Social / Messages',
  'Performance',
  'Autre',
];

export default function BetaReport() {
  const { user } = useUser();
  const navigate = useNavigate();
  const isAllowed = user && ['admin', 'superadmin', 'moderator', 'beta'].includes(user.accountType);
  const isAdmin = user && ['admin', 'superadmin'].includes(user.accountType);

  const [reports, setReports] = useState([]);
  const [pageUrl, setPageUrl] = useState('');
  const [category, setCategory] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [tab, setTab] = useState('new'); // 'new' | 'history'

  // Charger les rapports
  useEffect(() => {
    if (!isAllowed) return;

    let q;
    if (isAdmin) {
      q = query(collection(db, 'betaReports'), orderBy('createdAt', 'desc'));
    } else {
      q = query(collection(db, 'betaReports'), where('userId', '==', user.id), orderBy('createdAt', 'desc'));
    }

    const unsub = onSnapshot(q, (snap) => {
      setReports(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [isAllowed, isAdmin, user?.id]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (submitting || !title.trim() || !description.trim()) return;

    setSubmitting(true);
    try {
      await addDoc(collection(db, 'betaReports'), {
        userId: user.id,
        userName: user.name || user.username || 'Anonyme',
        pageUrl: pageUrl.trim(),
        category,
        severity,
        title: title.trim(),
        description: description.trim(),
        steps: steps.trim(),
        status: 'open',
        createdAt: new Date().toISOString(),
        device: navigator.userAgent.includes('Mobile') ? 'mobile' : 'desktop',
        browser: navigator.userAgent,
      });

      setSubmitted(true);
      setPageUrl('');
      setCategory('');
      setSeverity('medium');
      setTitle('');
      setDescription('');
      setSteps('');
      setTimeout(() => setSubmitted(false), 4000);
    } catch (err) {
      console.error('Erreur envoi rapport:', err);
    }
    setSubmitting(false);
  }, [submitting, user, pageUrl, category, severity, title, description, steps]);

  const updateStatus = async (reportId, status) => {
    await updateDoc(doc(db, 'betaReports', reportId), { status });
  };

  const deleteReport = async (reportId) => {
    await deleteDoc(doc(db, 'betaReports', reportId));
  };

  if (!isAllowed) return <NotFound />;

  const statusColors = { open: '#f59e0b', in_progress: '#3b82f6', resolved: '#22c55e', closed: '#999' };
  const statusLabels = { open: 'Ouvert', in_progress: 'En cours', resolved: 'Résolu', closed: 'Fermé' };

  return (
    <div className="page beta-page">
      <div className="beta-header">
        <div>
          <h1 className="beta-title"><IoBug /> Beta Report</h1>
          <p className="beta-subtitle">Signale les bugs pour améliorer Plumly</p>
        </div>
        <span className="beta-badge">{isAdmin ? '👑 Admin' : '🧪 Beta Testeur'}</span>
      </div>

      {/* Tabs */}
      <div className="beta-tabs">
        <button className={'beta-tab ' + (tab === 'new' ? 'beta-tab-active' : '')} onClick={() => setTab('new')}>
          <IoBug /> Nouveau rapport
        </button>
        <button className={'beta-tab ' + (tab === 'history' ? 'beta-tab-active' : '')} onClick={() => setTab('history')}>
          <IoTime /> Historique ({reports.length})
        </button>
      </div>

      {/* Formulaire */}
      {tab === 'new' && (
        <motion.form
          className="beta-form"
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="beta-field">
            <label>Titre du bug *</label>
            <input
              type="text"
              placeholder="Ex: Le bouton swipe ne fonctionne pas"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="beta-field">
            <label>Page concernée (URL)</label>
            <input
              type="text"
              placeholder="Ex: /library ou /read/abc123"
              value={pageUrl}
              onChange={(e) => setPageUrl(e.target.value)}
            />
          </div>

          <div className="beta-row">
            <div className="beta-field beta-field-half">
              <label>Catégorie</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="">-- Choisir --</option>
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="beta-field beta-field-half">
              <label>Sévérité</label>
              <div className="beta-severity-options">
                {SEVERITY.map(s => (
                  <button
                    key={s.id}
                    type="button"
                    className={'beta-severity-btn ' + (severity === s.id ? 'beta-severity-active' : '')}
                    style={{ '--sev-color': s.color }}
                    onClick={() => setSeverity(s.id)}
                  >
                    {s.emoji} {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="beta-field">
            <label>Description du bug *</label>
            <textarea
              placeholder="Décris le problème en détail : ce qui se passe, ce qui devrait se passer..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              required
            />
          </div>

          <div className="beta-field">
            <label>Étapes pour reproduire</label>
            <textarea
              placeholder="1. Aller sur la page...&#10;2. Cliquer sur...&#10;3. Le bug apparaît"
              value={steps}
              onChange={(e) => setSteps(e.target.value)}
              rows={3}
            />
          </div>

          <motion.button
            type="submit"
            className="beta-submit-btn"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            disabled={submitting || !title.trim() || !description.trim()}
          >
            {submitting ? 'Envoi...' : <><IoSend /> Envoyer le rapport</>}
          </motion.button>

          <AnimatePresence>
            {submitted && (
              <motion.div
                className="beta-success"
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
              >
                <IoCheckmarkCircle /> Rapport envoyé ! Merci pour ta contribution 🪶
              </motion.div>
            )}
          </AnimatePresence>
        </motion.form>
      )}

      {/* Historique */}
      {tab === 'history' && (
        <div className="beta-history">
          {reports.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🐛</div>
              <p className="empty-state-text">Aucun rapport pour le moment</p>
            </div>
          ) : (
            reports.map((report, i) => (
              <motion.div
                key={report.id}
                className="beta-report-card"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
              >
                <div className="beta-report-top">
                  <span className="beta-report-severity" style={{ background: SEVERITY.find(s => s.id === report.severity)?.color || '#999' }}>
                    {SEVERITY.find(s => s.id === report.severity)?.emoji}
                  </span>
                  <h3 className="beta-report-title">{report.title}</h3>
                  <span className="beta-report-status" style={{ color: statusColors[report.status] || '#999' }}>
                    {statusLabels[report.status] || report.status}
                  </span>
                </div>

                <p className="beta-report-desc">{report.description}</p>

                {report.pageUrl && <p className="beta-report-url">📍 {report.pageUrl}</p>}
                {report.category && <span className="beta-report-category">{report.category}</span>}

                <div className="beta-report-footer">
                  {isAdmin && <span className="beta-report-author">par {report.userName}</span>}
                  <span className="beta-report-date">{new Date(report.createdAt).toLocaleDateString('fr-FR')}</span>
                  <span className="beta-report-device">{report.device === 'mobile' ? '📱' : '💻'}</span>

                  {isAdmin && (
                    <div className="beta-report-admin-actions">
                      <select
                        value={report.status}
                        onChange={(e) => updateStatus(report.id, e.target.value)}
                        className="beta-status-select"
                      >
                        <option value="open">Ouvert</option>
                        <option value="in_progress">En cours</option>
                        <option value="resolved">Résolu</option>
                        <option value="closed">Fermé</option>
                      </select>
                      <button className="beta-report-delete" onClick={() => deleteReport(report.id)}>
                        <IoTrash />
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            ))
          )}
        </div>
      )}
    </div>
  );
}